import React from 'react';
import { Button } from 'antd';
import PropTypes, { Requireable } from 'prop-types';
import { useTranslation } from 'react-i18next';

type ButtonProps = {
  theme?: 'primary' | 'secondary';
  disabled?: boolean;
  children: React.ReactNode;
  primaryColor?: {
    backgroundColor?: string;
    color?: string;
    borderColor?: string;
  };
  secondaryColor?: {
    backgroundColor?: string;
    color?: string;
    borderColor?: string;
  };
  height?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  className: string;
  sizeType?: 'small' | 'medium' | 'large' | 'xlarge';
  htmlType?: 'submit' | 'button';
};

const sizeStylesMap: Record<'small' | 'medium' | 'large' | 'xlarge',
  {
    padding: string;
    fontSizeClasses: string;
  }
> = {
  small: {
    padding: '8px 16px',
    fontSizeClasses: 'xl:text-sm 3xl:text-base md:text-xs',
  },
  medium: {
    padding: '8px 16px',
    fontSizeClasses: 'xl:text-base 3xl:text-lg md:text-sm',
  },
  large: {
    padding: '12px 24px',
    fontSizeClasses: 'xl:text-lg 3xl:text-xl md:text-base',
  },
  xlarge: {
    padding: '12px 34px',
    fontSizeClasses: 'xl:text-lg 3xl:text-xl md:text-base',
  },
};

const PrimaryButton: React.FC<ButtonProps> = ({
  theme = 'primary',
  disabled = false,
  children,
  primaryColor = {},
  secondaryColor = {},
  className = '',
  height = '40px',
  onClick,
  sizeType = 'medium',
  htmlType = 'button',
  ...props
}) => {
  // Added onClick to props
  // Define styles for different themes
  const sizeStyles = sizeStylesMap[sizeType || 'medium'];
  const primaryStyles: React.CSSProperties = {
    ...sizeStyles,
    backgroundColor: primaryColor?.backgroundColor || '#1D4E89',
    color: primaryColor?.color || '#FFFFFF',
    borderColor: primaryColor?.borderColor || '#1D4E89',
    height: height,
  };
  const { t } = useTranslation();
  const secondaryStyles: React.CSSProperties = {
    ...sizeStyles,
    backgroundColor: secondaryColor?.backgroundColor || 'transparent',
    color: secondaryColor?.color || '#0298A0',
    borderColor: secondaryColor?.borderColor || '#0298A0',
    height: height,
  };

  const disabledStyles: React.CSSProperties = {
    ...sizeStyles,
    backgroundColor: '#E5E5E5',
    color: '#A3A3A3',
    borderColor: '#737373',
    height: height,
  };

  const disabledSecondaryStyles: React.CSSProperties = {
    ...sizeStyles,
    backgroundColor: 'transparent',
    color: '#A3A3A3',
    borderColor: '#737373',
    height: height,
  };
  // Apply styles based on theme and disabled state
  const buttonStyles: React.CSSProperties =
    disabled && theme === 'primary'
      ? disabledStyles
      : disabled && theme === 'secondary'
        ? disabledSecondaryStyles
        : theme === 'primary'
          ? primaryStyles
          : secondaryStyles;

  return (
    <Button
      style={buttonStyles}
      disabled={disabled}
      onClick={onClick}
      htmlType={htmlType}
      {...props}
      className={`${sizeStyles.fontSizeClasses} ${className}`}
    >
      {children}
    </Button>
  );
};

export default PrimaryButton;
